import { UILanguageCode } from "../types";

const en = {
  languageCode: "en" as UILanguageCode,
  projects: {
    addTo: "Add to project",
    all: "All projects",
    new: "New project",
    singular: "Project",
    plural: "Projects",
    my: "My Projects",
    createNew: "Create new project",
    infos: "Project infos",
    delete: "Delete project",
    removeShared: "Remove shared project",
    search: "Search projects",
    shareWithUser: "Share project with user",
    allowUserToEditSharedConfiguration: "Allow user to edit shared configuration",
    allowUserToEditSharedProjectAndConfigurations:
      "Allow user to edit shared project and the containing configurations",
    editSharings: "Edit sharings",
    invitation: "Project invitation",
    invitations: "Project invitations",
    invite: "Invite to project",
    sharings: "Project sharings",
    shared: "Shared project",
    sentAt: "Sent",
    resendInvite: "Resend invite",
    invitePage: {
      badge: "Project access invitation",
      loadingProjectDetails: "Loading project details...",
      collaborationLead: "invited you to collaborate on this project and review its shared content.",
      projectName: "Project name",
      inviteNoLongerAvailable:
        "This invitation is no longer available because it has already been accepted, rejected, or cancelled.",
      acceptSignedIn: "Accepting this invitation will open the project immediately.",
      acceptSignedOut: "Accepting this invitation will ask you to sign in before granting access to the project.",
      inviteNotFound: "This invitation could not be found. It may have been removed or the link may be incorrect.",
    },

    invitationStatus: {
      pending: "Pending",
      active: "Active",
      accepted: "Accepted",
      rejected: "Rejected",
      cancelled: "Cancelled",

      statusChanges: {
        rejected: "Invitation declined. Your email address has been anonymized and removed from this project.",
        accept: "Invite accepted successfully",
      },
    },

    questions: {
      delete: "Do you really want to delete this project?",
      unshare: "Do you really want to unshare the project for this user?",
      cancelInvite: "Do you really want to cancel this pending invitation?",
      removeShared:
        "Do you really want to remove this project from your shared projects? You will lose access to it, and this action cannot be undone. The project itself and its configurations will not be affected.",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "It is not possible to delete a project if configurations are present.",
      deleteProjectWithDocumentsNotPossible: "It is not possible to delete a project if documents are present.",
      notFound: "Project not found.",
      sharingNotFound: "Sharing not found.",
      sharingAlreadyExists: "This project is already shared with this user.",
      inviteUpdateNotAllowed: "You are not allowed to accept or refuse this invite.",
      inviteAlreadyExists: "An invite for this user already exists.",
      inviteAlreadyCancelled: "This invite has already been cancelled.",
      inviteAlreadyAccepted: "This invite has already been accepted.",
      inviteAlreadyRejected: "This invite has already been rejected.",
      documentNotFound: "Document not found.",
      missingDocumentName: "Document name is missing.",
      documentAlreadyPresent: "Document already present.",
      missingProjectStatus: "Project status is missing.",
    },
  },

  configurations: {
    all: "All configurations",
    new: "New configuration",
    plural: "Configurations",
    create: "Create configuration",
    createNew: "Create new configuration",
    delete: "Delete configuration",
    removeShared: "Remove shared configuration",
    duplicate: "Duplicate configuration",
    addToProject: "Add to project",
    unlinkFromProject: "Unlink from project",
    my: "My Configurations",
    search: "Search configurations",
    reset: "Reset configuration",

    questions: {
      delete: "Do you really want to delete this configuration?",
      reset: "Do you really want to reset all the data of the current configuration?",
      unlinkFromProject: "Do you really want to unlink this configuration from its project?",
      removeShared:
        "Do you really want to remove this configuration from your shared configurations? You will lose access to it, and this action cannot be undone. The configuration itself will not be affected.",
    },

    invitePage: {
      badge: "Configuration access invitation",
      loadingConfigurationDetails: "Loading configuration details...",
      collaborationLead: "invited you to collaborate on this configuration and review its shared content.",
      configurationName: "Configuration name",
      inviteNoLongerAvailable:
        "This invitation is no longer available because it has already been accepted, rejected, or cancelled.",
      acceptSignedIn: "Accepting this invitation will open the configuration immediately.",
      acceptSignedOut: "Accepting this invitation will ask you to sign in before granting access to the configuration.",
      inviteNotFound: "This invitation could not be found. It may have been removed or the link may be incorrect.",
    },

    invitationStatus: {
      statusChanges: {
        rejected: "Invitation declined. Your email address has been anonymized and removed from this configuration.",
        accept: "Invite accepted successfully",
      },
    },

    errors: {
      notFound: "Configuration not found.",
      missingNormative: "Configuration normative is missing.",
      missingSteelVersion: "Configuration steel version is missing.",
      missingUpperSupportInstallationType: "Configuration upper support installation type is missing.",
      missingUpperSupport: "Configuration upper support is missing.",
      missingAbsorber: "Configuration absorber is missing.",
      missingSystemType: "Configuration system type is missing.",
      missingCable: "Configuration cable is missing.",
      alreadyLinkedToProject: "This configuration is already linked to a project.",
      notLinkedToProject: "This configuration is not linked to a project.",
    },
  },

  documents: {
    add: "Add document",
    all: "All documents",
    alreadyPresent: "Document already present.",
    plural: "Documents",
    delete: "Delete document",
    search: "Search documents",

    questions: {
      delete: "Do you really want to delete this document?",
      overwrite: "Do you want to overwrite the already present document?",
    },

    errors: {
      someCouldNotBeSaved: "Some documents could not be saved",
    },
  },

  systems: {
    all: "All systems",
    new: "New system",
    singular: "System",
    plural: "Systems",
    delete: "Delete system",
    search: "Search systems",
    our: "Our systems",

    questions: {
      delete: "Do you really want to delete this system?",
    },

    errors: {
      notFound: "System not found.",
      inUseByConfiguration: "The system is still in use by a configuration.",
    },
  },

  entityAccesses: {
    title: "User permissions",
    add: "Add user permission",
    accessColumn: "Access",
    questions: {
      remove: "Do you really want to remove this user's access?",
    },
    errors: {
      alreadyExists: "This user already has a permission for this item.",
    },
  },

  users: {
    add: "Add user",
    all: "All users",
    iam: {
      label: "IAM",
      missing: "Missing",
      valid: "Valid",
    },
    invite: "Invite user",
    plural: "Users",
    remove: "Remove user",
    search: "Search users",
    usersEmail: "Users email",
    usersName: "Users name",

    questions: {},

    errors: {
      notFound: "User not found.",
      notFoundInAccounts: "User not found in accounts.",
    },
  },

  items: {
    all: "All items",
    singular: "Item",
    plural: "Items",
    createNew: "Create new item",
    delete: "Delete item",
    search: "Search items",
    list: "Items list",

    questions: {
      delete: "Do you really want to delete this item?",
    },

    errors: {
      notFound: "Item not found.",
      doesNotExist: "Item with this code does not exits.",
      missingCode: "Item code is missing.",
    },
  },

  absorbers: {
    singular: "Absorber",
    plural: "Absorbers",
    createNew: "Create new absorber",
    delete: "Delete absorber",
    removeFromSystem: "Remove absorber from system",
    edit: "Edit absorber",
    search: "Search absorbers",

    questions: {
      delete: "Do you really want to delete this absorber?",
      removeFromSystem: "Do you really want to remove this absorber from the system?",
    },

    errors: {
      alreadyPresent: "Absorber already present.",
      notFound: "Absorber not found.",
      noDefined: "No absorbers defined.",
      inUseByConfiguration: "Absorber is still in use by a configuration.",
    },
  },

  cables: {
    createNew: "Create new cable",
    delete: "Delete cable",
    edit: "Edit cable",
    search: "Search cables",
    singular: "Cable",
    plural: "Cables",

    questions: {
      delete: "Do you really want to delete this cable?",
    },

    errors: {
      alreadyPresent: "Cable already present.",
      notFound: "Cable not found.",
      noDefined: "No cables defined.",
      inUseByConfiguration: "Cable is still in use by a configuration.",
    },
  },

  elements: {
    createNew: "Create new element",
    delete: "Delete element",
    edit: "Edit element",
    singular: "Element",
    plural: "Elements",
    search: "Search elements",

    questions: {
      delete: "Do you really want to delete this element?",
    },

    errors: {
      alreadyPresent: "Element already present.",
      notFound: "Element not found.",
      inUseByConfiguration: "Element is still in use by a configuration.",
    },
  },

  handles: {
    addItemAsVertigrip: "Add item as Vertigrip handle",
    delete: "Delete handle",
    edit: "Edit handle",
    singular: "Handle",
    plural: "Handles",
    search: "Search handles",
    noHandle: "No handle",

    questions: {
      delete: "Do you really want to delete this handle?",
    },

    errors: {
      alreadyPresent: "Handle already present.",
      notFound: "Handle not found.",
      inUseByConfiguration: "Handle is still in use by a configuration.",
      inUseByUpperExtension: "Handle is still in use by an upper extension.",
    },
  },

  intermediateElements: {
    createNew: "Create new intermediate element",
    delete: "Delete intermediate element",
    edit: "Edit intermediate element",
    singular: "Intermediate element",
    plural: "Intermediate elements",
    search: "Search intermediate elements",

    questions: {
      delete: "Do you really want to delete this intermediate element?",
    },

    errors: {
      alreadyPresent: "Intermediate element already present.",
      notFound: "Intermediate element not found.",
      inUseByConfiguration: "Intermediate element is still in use by a configuration.",
    },
  },

  ladderReinforcements: {
    createNew: "Create new ladder reinforcement",
    delete: "Delete ladder reinforcement",
    edit: "Edit ladder reinforcement",
    singular: "Ladder reinforcement",
    plural: "Ladder reinforcements",
    search: "Search ladder reinforcements",
    noLadderReinforcement: "No ladder reinforcement",

    questions: {
      delete: "Do you really want to delete this ladder reinforcement?",
    },

    errors: {
      alreadyPresent: "Ladder reinforcement already present.",
      notFound: "Ladder reinforcement not found.",
      inUseByConfiguration: "Ladder reinforcement is still in use by a configuration.",
    },
  },

  wallMaterials: {
    createNew: "Create new wall material",
    delete: "Delete wall material",
    singular: "Wall material",
    plural: "Wall materials",
    search: "Search wall materials",
    noWallMaterial: "No wall material",

    questions: {
      delete: "Do you really want to delete this wall material?",
    },

    errors: {
      notFound: "Wall material not found.",
      inUseByConfiguration: "Wall material is still in use by a configuration.",
    },
  },

  wallMaterialFixings: {
    createNew: "Create new fixing",
    delete: "Delete fixing",
    singular: "Fixing",
    plural: "Fixings",
    search: "Search fixings",

    questions: {
      delete: "Do you really want to delete this fixing?",
    },

    errors: {
      notFound: "Fixing not found.",
      inUseByConfiguration: "Fixing is still in use by a configuration.",
    },
  },

  wallMaterialFixingItems: {
    errors: {
      alreadyPresent: "Fixing already present.",
    },
  },

  lowerElements: {
    createNew: "Create new lower element",
    delete: "Delete lower element",
    edit: "Edit lower element",
    singular: "Lower element",
    plural: "Lower elements",
    search: "Search lower elements",

    questions: {
      delete: "Do you really want to delete this lower element?",
    },

    errors: {
      alreadyPresent: "Lower element already present.",
      notFound: "Lower element not found.",
      inUseByConfiguration: "Lower element is still in use by a configuration.",
    },
  },

  shuttles: {
    addItemAsVertigrip: "Add item as Vertigrip shuttle",
    addItemAsPatrol: "Add item as Patrol shuttle",
    delete: "Delete shuttle",
    edit: "Edit shuttle",
    singular: "Shuttle",
    plural: "Shuttles",
    search: "Search shuttles",
    no: "No shuttle",

    questions: {
      delete: "Do you really want to delete this shuttle?",
    },

    errors: {
      alreadyPresent: "Shuttle already present.",
      notFound: "Shuttle not found.",
      inUseByConfiguration: "Shuttle is still in use by a configuration.",
    },
  },

  terminals: {
    createNew: "Create new terminal",
    delete: "Delete terminal",
    edit: "Edit terminal",
    singular: "Terminal",
    plural: "Terminals",
    search: "Search terminals",

    questions: {
      delete: "Do you really want to delete this terminal?",
    },

    errors: {
      alreadyPresent: "Terminal already present.",
      notFound: "Terminal not found.",
      inUseByConfiguration: "Terminal is still in use by a configuration.",
    },
  },

  installationTypes: {
    plural: "Installation types",
    singular: "Installation type",
    search: "Search installation types",

    questions: {},

    errors: {
      notFound: "Installation type not found.",
      inUseByConfiguration: "Installation type is still in use by a configuration.",
      inUseByUpperExtension: "Installation type is still in use by an upperExtension.",
      inUseByLowerElement: "Installation type is still in use by a lower element.",
      inUseByIntermediateElement: "Installation type is still in use by an intermediate element.",
      inUseByLadderReinforcement: "Installation type is still in use by a ladder reinforcement.",
    },
  },

  steelVersions: {
    createNew: "Create new steel version",
    delete: "Delete steel version",
    singular: "Steel version",
    plural: "Steel versions",
    search: "Search steel versions",

    questions: {
      delete: "Do you really want to delete this steel version?",
    },

    warnings: {
      deleteWarning: "Deleting this data will affect other modules/configurations.",
      editWarning: "Editing this data will affect other modules/configurations.",
    },

    errors: {
      notFound: "Steel version not found.",
      inUseByConfiguration: "Steel version is still in use by a configuration.",
      inUseByUpperExtension: "Steel version is still in use by an upperExtension.",
      inUseByLowerElement: "Steel version is still in use by a lower element.",
      inUseByIntermediateElement: "Steel version is still in use by an intermediate element.",
      inUseByAsorber: "Steel version is still in use by an absorber.",
      inUseBySupport: "Steel version is still in use by a support.",
      inUseByCurvesIntermediate: "Steel version is still in use by a curve/intermediate.",
      inUseByTerminal: "Steel version is still in use by a terminal.",
      inUseByShuttle: "Steel version is still in use by a shuttle.",
      inUseByAnchorMapping: "Steel version is still in use by an anchor mapping.",
      inUseByConfigurationAnchorPoint: "Steel version is still in use by a configuration anchor point.",
    },
  },

  upperExtensions: {
    createNew: "Create new upper extension",
    delete: "Delete upper extension",
    edit: "Edit upper extension",
    singular: "Upper extension",
    plural: "Upper extensions",
    search: "Search upper extensions",

    questions: {
      delete: "Do you really want to delete this upper extension?",
    },

    errors: {
      alreadyPresent: "Upper extension already present.",
      notFound: "Upper extension not found.",
      noDefined: "No upper extensions defined.",
      inUseByConfiguration: "Upper extension is still in use by a configuration.",
    },
  },

  normatives: {
    createNew: "Create new normative",
    delete: "Delete normative",
    singular: "Normative",
    plural: "Normatives",
    search: "Search normatives",
    add: "Add normative",

    questions: {
      delete: "Do you really want to delete this normative?",
    },

    errors: {
      notFound: "Normative not found.",
      noDefined: "No normatives defined.",
      inUseByConfiguration: "Normative is still in use by a configuration.",
    },
  },

  clampings: {
    createNew: "Create new clamping",
    edit: "Edit clamping",
    delete: "Delete clamping",
    singular: "Clamping",
    plural: "Clampings",
    search: "Search clampings",

    questions: {
      delete: "Do you really want to delete this clamping?",
    },

    errors: {
      notFound: "Clamping not found.",
      inUseByConfiguration: "Clamping is still in use by a configuration.",
    },
  },

  systemTypes: {
    singular: "System type",
    plural: "System types",
    search: "Search system types",

    questions: {},

    errors: {
      notFound: "System type not found.",
      inUseByConfiguration: "System type is still in use by a configuration.",
      inUseByShuttle: "System type is still in use by a shuttle.",
      inUseByIntermediateElement: "System type is still in use by an intermediate element.",
    },
  },

  roofTypes: {
    createNew: "Create new roof type",
    delete: "Delete roof type",
    singular: "Roof type",
    plural: "Roof types",
    search: "Search roof types",

    questions: {
      delete: "Do you really want to delete this roof type?",
    },

    errors: {
      pleaseSelectRoofType: "Please select a roof type.",
    },
  },

  roofCategories: {
    createNew: "Create new roof category",
    delete: "Delete roof category",
    singular: "Roof category",
    plural: "Roof categories",
    search: "Search roof categories",

    questions: {
      delete: "Do you really want to delete this roof category?",
    },

    errors: {
      pleaseSelectRoofCategory: "Please select a roof type.",
      inUseByRoofType: "Category is still in use by a roof type.",
    },
  },

  roofMaterials: {
    createNew: "Create new roof material",
    delete: "Delete roof material",
    singular: "Roof material",
    plural: "Roof materials",
    search: "Search roof materials",

    questions: {
      delete: "Do you really want to delete this roof material?",
    },
  },

  materials: {
    createNew: "Create new material",
    delete: "Delete material",
    singular: "Material",
    plural: "Materials",
    search: "Search materials",

    questions: {
      delete: "Do you really want to delete this material?",
    },
  },

  roofSubstructures: {
    createNew: "Create new roof substructure",
    delete: "Delete roof substructure",
    singular: "Roof substructure",
    plural: "Roof substructures",
    single: "Roof substructure",
    search: "Search roof substructures",

    questions: {
      delete: "Do you really want to delete this roof substructure?",
    },
  },

  membranes: {
    createNew: "Create new membrane",
    delete: "Delete membrane",
    singular: "Membrane",
    plural: "Membranes",
    single: "Membrane",
    search: "Search membranes",

    questions: {
      delete: "Do you really want to delete this membrane?",
    },
  },

  sheetMaterials: {
    createNew: "Create new sheet material",
    delete: "Delete sheet material",
    edit: "Edit sheet material",
    singular: "Sheet material",
    plural: "Sheet materials",
    search: "Search sheet materials",

    questions: {
      delete: "Do you really want to delete this sheet material?",
    },
  },

  patrolSystems: {
    createNew: "Create new system",
    delete: "Delete system",
    singular: "System",
    plural: "Systems",
    search: "Search systems",

    questions: {
      delete: "Do you really want to delete this system?",
    },
  },

  patrolSystemMappings: {
    createNew: "Create new system mapping",
    delete: "Delete system mapping",
    edit: "Edit system mapping",
    singular: "System mapping",
    plural: "System mappings",
    search: "Search system mappings",

    questions: {
      delete: "Do you really want to delete this system mapping?",
    },
  },

  systemNormatives: {
    createNew: "Create new system normative",
    delete: "Delete system normative",
    edit: "Edit system normative",
    singular: "System normative",
    plural: "System normatives",
    search: "Search system normatives",

    questions: {
      delete: "Do you really want to delete this system normative?",
    },

    errors: {
      normativeAlreadyPresent: "The selected normative is already associated to the system",
    },
  },

  uprightSpanMappings: {
    createNew: "Create new upright span mapping",
    delete: "Delete upright span mapping",
    edit: "Edit upright span mapping",
    singular: "Upright span mapping",
    plural: "Upright span mappings",
    search: "Search upright span mappings",
    questions: {
      delete: "Do you really want to delete this upright span mapping?",
    },
    errors: {
      alreadyPresent: "Upright span mapping already present",
      updateNotAllowed: "You are not allowed to update this upright span mapping.",
    },
  },

  uprightMappings: {
    createNew: "Create new upright mapping",
    delete: "Delete upright mapping",
    edit: "Edit upright mapping",
    singular: "Upright mapping",
    plural: "Upright mappings",
    search: "Search upright mappings",
    questions: {
      delete: "Do you really want to delete this upright mapping?",
    },
    errors: {
      alreadyPresent: "Upright mapping already present",
    },
  },

  patrolAnchorMappings: {
    createNew: "Create new anchor mapping",
    delete: "Delete anchor mapping",
    edit: "Edit anchor mapping",
    singular: "Anchor mapping",
    plural: "Anchor mappings",
    search: "Search anchor mappings",

    questions: {
      delete: "Do you really want to delete this anchor mapping?",
    },
  },

  patrolSupports: {
    createNew: "Create new support",
    delete: "Delete support",
    edit: "Edit support",
    singular: "Support",
    plural: "Supports",
    search: "Search supports",

    questions: {
      delete: "Do you really want to delete this support?",
    },
  },

  itemNormativeExclusions: {
    add: "Add excluded item",
    createNew: "Create new item normative exclusion",
    delete: "Delete item normative exclusion",
    edit: "Edit item normative exclusion",
    singular: "Item normative exclusion",
    plural: "Item normative exclusions",
    search: "Search item normative exclusions",

    questions: {
      remove: "Do you really want to remove this excluded item?",
    },

    errors: {
      notFound: "Item normative exclusion not found.",
    },
  },

  supportExtensions: {
    singular: "Support extension",
    plural: "Support extensions",
    createNew: "Create new support extension",
    delete: "Delete support extension",
    edit: "Edit support extension",
    search: "Search support extensions",

    questions: {
      delete: "Do you really want to delete this support extension?",
    },
  },

  supportExtras: {
    singular: "Support extra",
    plural: "Support extras",
    createNew: "Create new support extra",
    delete: "Delete support extra",
    edit: "Edit support extra",
    search: "Search support extras",

    questions: {
      delete: "Do you really want to delete this support extra?",
    },
  },

  supportOptionals: {
    singular: "Support optional",
    plural: "Support optionals",
    createNew: "Create new support optional",
    delete: "Delete support optional",
    edit: "Edit support optional",
    search: "Search support optionals",

    questions: {
      delete: "Do you really want to delete this support optional?",
    },
  },

  // patrolIntermediates: {
  //   singular: "Intermediate",
  //   plural: "Intermediates",
  //   createNew: "Create new intermediate",
  //   delete: "Delete intermediate",
  //   edit: "Edit intermediate",
  //   search: "Search intermediates",

  //   questions: {
  //     delete: "Do you really want to delete this intermediate?",
  //   },
  // },

  // patrolCurves: {
  //   singular: "Curve",
  //   plural: "Curves",
  //   createNew: "Create new curve",
  //   delete: "Delete curve",
  //   edit: "Edit curve",
  //   search: "Search curves",

  //   questions: {
  //     delete: "Do you really want to delete this curve?",
  //   },
  // },

  patrolCurveIntermediates: {
    singular: "Curve/Intermediate",
    plural: "Curves/Intermediates",
    createNew: "Create new curve/intermediate",
    delete: "Delete curve/intermediate",
    edit: "Edit curve/intermediate",
    search: "Search curve/intermediates",

    questions: {
      delete: "Do you really want to delete this curve/intermediate?",
    },
  },

  texts: {
    singular: "Text",
    plural: "Texts",
    search: "Search texts",

    questions: {},

    errors: {},
  },

  clients: {
    all: "All clients",
    plural: "Clients",
    createNew: "Create new client",
    delete: "Delete client",
    search: "Search clients",

    questions: {
      delete: "Do you really want to delete this client?",
    },

    errors: {
      notFound: "Client not found.",
      insertNotAllowed: "You are not allowed to insert a new client.",
      readNotAllowed: "You are not allowed to read this client.",
      updateNotAllowed: "You are not allowed to update this client.",
      deleteNotAllowed: "You are not allowed to delete this client.",
    },
  },

  roles: {
    plural: "Roles",
  },

  permissions: {
    plural: "Permissions",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingValue: "Secret value is missing.",
    },
  },

  normativeVersions: {
    singular: "Normative version",
    plural: "Normative versions",
    add: "Add normative version",
    createNew: "Create new normative version",

    errors: {
      atLeastOneSystemRequired: "At least one system is required.",
    },
  },

  anchorPoints: {
    addNew: "Add new anchor points",
    singular: "Anchor point",
    plural: "Anchor points",
    delete: "Delete anchor point",
    hide: "Hide anchor points",
    show: "Show anchor points",

    questions: {
      delete: "Do you really want to delete the selected anchor point?",
    },
  },

  accessPoints: {
    addNew: "Add new access points",
    singular: "Access point",
    // plural: "Access points",
    delete: "Delete access point",
    // hide: "Hide access points",
    // show: "Show access points",

    questions: {
      delete: "Do you really want to delete the selected access point?",
    },
  },

  lifeLines: {
    draw: "Draw lifeline",
    singular: "Lifeline",
    plural: "Lifelines",
    delete: "Delete lifeline",
    hide: "Hide lifelines",
    show: "Show lifelines",

    questions: {
      deleteEdge: "Do you really want to delete the selected segment?",
      deleteNode: "Do you really want to delete the selected intersection point?",
    },

    errors: {
      creatingCrossIntersectionsNotAllowed: "To create a four cross intersection is not allowed.",
      loopsNotAllowed: "The current selected system does not allow loops.",
      intersectionsNotAllowed: "The current selected system does not allow 3-way intersections.",
      systemDoesNotAllowMoreThanXCorners: (maxCorners: number) =>
        `The current selected system does not allow more than ${maxCorners} corners`,
    },
  },

  substructures: {
    createNew: "Create new substructure",
    delete: "Delete substructure",
    singular: "Substructure",
    plural: "Substructures",
    single: "Substructure",
    search: "Search substructures",
    edit: "Edit substructure",

    questions: {
      delete: "Do you really want to delete this substructure?",
    },
  },

  bases: {
    createNew: "Create new base",
    delete: "Delete base",
    singular: "Base",
    plural: "Bases",
    search: "Search bases",
    edit: "Edit base",
    add: "Add base",

    questions: {
      delete: "Do you really want to delete this base?",
    },

    errors: {
      alreadyPresent: "Base already present",
      notFound: "Base not found",
    },
  },

  ballasts: {
    createNew: "Create new ballast",
    delete: "Delete ballast",
    singular: "Ballast",
    plural: "Ballasts",
    search: "Search ballasts",
    edit: "Edit ballast",
    add: "Add ballast",

    questions: {
      delete: "Do you really want to delete this ballast?",
    },

    errors: {
      alreadyPresent: "Ballast already present",
      notFound: "Ballast not found",
    },
  },

  ballastTypes: {
    createNew: "Create new ballast type",
    delete: "Delete ballast type",
    singular: "Ballast type",
    plural: "Ballast types",
    search: "Search ballast types",
    edit: "Edit ballast type",

    questions: {
      delete: "Do you really want to delete this ballast type?",
    },

    errors: {
      missingName: "Ballast type name is missing",
      notFound: "Ballast type not found",
    },
  },

  uprights: {
    createNew: "Create new upright",
    delete: "Delete upright",
    singular: "Upright",
    plural: "Uprights",
    search: "Search uprights",
    edit: "Edit upright",
    add: "Add upright",

    questions: {
      delete: "Do you really want to delete this upright?",
    },

    errors: {
      alreadyPresent: "Upright already present",
      notFound: "Upright not found",
    },
  },

  uprightTypes: {
    createNew: "Create new upright type",
    delete: "Delete upright type",
    singular: "Upright type",
    plural: "Upright types",
    search: "Search upright types",
    edit: "Edit upright type",
    add: "Add upright type",

    questions: {
      delete: "Do you really want to delete this upright type?",
    },

    errors: {
      missingName: "Upright type name is required",
      notFound: "Upright type not found",
      inUseByUprights: "Upright type is still used by one or more uprights",
    },
  },

  guardrailCompositions: {
    createNew: "Create new guardrail composition",
    delete: "Delete guardrail composition",
    singular: "Guardrail composition",
    plural: "Guardrail compositions",
    search: "Search guardrail compositions",
    edit: "Edit guardrail composition",
    add: "Add guardrail composition",

    questions: {
      delete: "Do you really want to delete this guardrail composition?",
    },

    errors: {
      missingName: "Guardrail composition name is missing",
      notFound: "Guardrail composition not found",
    },
  },

  rails: {
    createNew: "Create new rail",
    delete: "Delete rail",
    singular: "Rail",
    plural: "Rails",
    search: "Search rails",
    edit: "Edit rail",
    add: "Add rail",

    questions: {
      delete: "Do you really want to delete this rail?",
    },

    errors: {
      alreadyPresent: "Rail already present",
      notFound: "Rail not found",
    },
  },

  toeBoards: {
    createNew: "Create new toe board",
    delete: "Delete toe board",
    singular: "Toe board",
    plural: "Toe boards",
    search: "Search toe boards",
    edit: "Edit toe board",
    add: "Add toe board",

    questions: {
      delete: "Do you really want to delete this toe board?",
    },

    errors: {
      alreadyPresent: "Toe board already present",
      notFound: "Toe board not found",
    },
  },

  toeBoardExtras: {
    createNew: "Create new toe board extra",
    delete: "Delete toe board extra",
    singular: "Toe board extra",
    plural: "Toe board extras",
    search: "Search toe board extras",
    edit: "Edit toe board extra",
    add: "Add toe board extra",

    questions: {
      delete: "Do you really want to delete this toe board extra?",
    },

    errors: {
      alreadyPresent: "Toe board extra already present",
      notFound: "Toe board extra not found",
    },
  },

  uprightCaps: {
    createNew: "Create new upright cap",
    delete: "Delete upright cap",
    singular: "Upright cap",
    plural: "Upright caps",
    search: "Search upright caps",
    edit: "Edit upright cap",
    add: "Add upright cap",

    questions: {
      delete: "Do you really want to delete this upright cap?",
    },

    errors: {
      alreadyPresent: "Upright cap already present",
      notFound: "Upright cap not found",
    },
  },

  railCaps: {
    createNew: "Create new rail cap",
    delete: "Delete rail cap",
    singular: "Rail cap",
    plural: "Rail caps",
    search: "Search rail caps",
    edit: "Edit rail cap",
    add: "Add rail cap",

    questions: {
      delete: "Do you really want to delete this rail cap?",
    },

    errors: {
      alreadyPresent: "Rail cap already present",
      notFound: "Rail cap not found",
    },
  },

  railWallFixings: {
    createNew: "Create new rail wall fixing",
    delete: "Delete rail wall fixing",
    singular: "Rail wall fixing",
    plural: "Rail wall fixings",
    search: "Search rail wall fixings",
    edit: "Edit rail wall fixing",
    add: "Add rail wall fixing",

    questions: {
      delete: "Do you really want to delete this rail wall fixing?",
    },

    errors: {
      alreadyPresent: "Rail wall fixing already present",
      notFound: "Rail wall fixing not found",
    },
  },

  railCorners: {
    createNew: "Create new rail corner",
    delete: "Delete rail corner",
    singular: "Rail corner",
    plural: "Rail corners",
    search: "Search rail corners",
    edit: "Edit rail corner",
    add: "Add rail corner",

    questions: {
      delete: "Do you really want to delete this rail corner?",
    },

    errors: {
      alreadyPresent: "Rail corner already present",
      notFound: "Rail corner not found",
    },
  },

  gates: {
    createNew: "Create new gate",
    delete: "Delete gate",
    singular: "Gate",
    plural: "Gates",
    search: "Search gates",
    edit: "Edit gate",
    add: "Add gate",

    questions: {
      delete: "Do you really want to delete this gate?",
    },

    errors: {
      alreadyPresent: "Gate already present",
      notFound: "Gate not found",
    },
  },

  waterproofKits: {
    createNew: "Create new waterproof kit",
    delete: "Delete waterproof kit",
    singular: "Waterproof kit",
    plural: "Waterproof kits",
    search: "Search waterproof kits",
    edit: "Edit waterproof kit",
    add: "Add waterproof kit",

    questions: {
      delete: "Do you really want to delete this waterproof kit?",
    },

    errors: {
      alreadyPresent: "Waterproof kit already present",
      notFound: "Waterproof kit not found",
    },
  },

  fastenings: {
    singular: "Fastening",
  },

  fasteningChoices: {
    createNew: "Create new fastening combination",
    delete: "Delete fastening combination",
    edit: "Edit fastening combination",
    singular: "Fastening combination",
    plural: "Fastening combinations",
    search: "Search fastening combinations",
    combination: "Combination",
    factorPerUpright: "Factor per upright",

    questions: {
      delete: "Do you really want to delete this fastening combination?",
    },

    errors: {
      alreadyPresent: "Fastening combination already present",
      duplicateFastening: "Duplicate fastening selected",
      notFound: "Fastening combination not found",
    },
  },

  membraneProtections: {
    createNew: "Create new membrane protection",
    delete: "Delete membrane protection",
    singular: "Membrane protection",
    plural: "Membrane protections",
    search: "Search membrane protections",
    edit: "Edit membrane protection",
    add: "Add membrane protection",

    questions: {
      delete: "Do you really want to delete this membrane protection?",
    },

    errors: {
      alreadyPresent: "Membrane protection already present",
      notFound: "Membrane protection not found",
    },
  },

  workMethods: {
    singular: "Work Method",
    plural: "Work Methods",
    deleteLabel: "work method",
    new: "New Work Method",
    edit: "Edit Work Method",
    delete: "Delete Work Method",
    search: "Search work methods",
  },

  supports: {
    singular: "Support",
    plural: "Supports",
    deleteLabel: "support",
    new: "New Support",
    edit: "Edit Support",
    delete: "Delete Support",
    search: "Search supports",
  },
  labelLanguages: {
    singular: "Label Language",
    plural: "Label Languages",
    deleteLabel: "label language",
    new: "New Label Language",
    edit: "Edit Label Language",
    delete: "Delete Label Language",
    search: "Search label languages",
  },
  towers: {
    singular: "Tower",
    plural: "Towers",
    deleteLabel: "tower",
    new: "New Tower",
    edit: "Edit Tower",
    delete: "Delete Tower",
    search: "Search towers",
  },
  trolleys: {
    singular: "Trolley",
    plural: "Trolleys",
    deleteLabel: "trolley",
    new: "New Trolley",
    edit: "Edit Trolley",
    delete: "Delete Trolley",
    search: "Search trolleys",
  },

  railCurves: {
    singular: "Rail Curve",
    plural: "Rail Curves",
    deleteLabel: "rail curve",
    new: "New Rail Curve",
    edit: "Edit Rail Curve",
    delete: "Delete Rail Curve",
    search: "Search rail curves",
  },
  railJunctions: {
    singular: "Rail Junction",
    plural: "Rail Junctions",
    deleteLabel: "rail junction",
    new: "New Rail Junction",
    edit: "Edit Rail Junction",
    delete: "Delete Rail Junction",
    search: "Search rail junctions",
  },
  railTerminals: {
    singular: "Rail Terminal",
    plural: "Rail Terminals",
    deleteLabel: "rail terminal",
    new: "New Rail Terminal",
    edit: "Edit Rail Terminal",
    delete: "Delete Rail Terminal",
    search: "Search rail terminals",
  },
  screws: {
    singular: "Screw",
    plural: "Screws",
    deleteLabel: "screw",
    new: "New Screw",
    edit: "Edit Screw",
    delete: "Delete Screw",
    search: "Search screws",
  },
  tools: {
    singular: "Tool",
    plural: "Tools",
    deleteLabel: "tool",
    new: "New Tool",
    edit: "Edit Tool",
    delete: "Delete Tool",
    search: "Search tools",
  },
  fasteningOptions: {
    singular: "Fastening Option",
    plural: "Fastening Options",
    deleteLabel: "fastening option",
    new: "New Fastening Option",
    edit: "Edit Fastening Option",
    delete: "Delete Fastening Option",
    search: "Search fastening options",
  },
  fasteningOptionItems: {
    singular: "Fastening Option Item",
    plural: "Fastening Option Items",
    deleteLabel: "fastening option item",
    new: "New Fastening Option Item",
    edit: "Edit Fastening Option Item",
    delete: "Delete Fastening Option Item",
    search: "Search fastening option items",
  },
  itemExtras: {
    singular: "Extra Item",
    plural: "Extras",
    deleteLabel: "extra item",
    new: "New Extra Item",
    edit: "Edit Extra Item",
    delete: "Delete Extra Item",
    search: "Search extras",
  },
  supportItems: {
    singular: "Support Item",
    plural: "Support Items",
    deleteLabel: "support item",
    new: "New Support Item",
    edit: "Edit Support Item",
    delete: "Delete Support Item",
    search: "Search support items",
  },
  systemSupportOptions: {
    singular: "System Support Option",
    plural: "System Support Options",
    deleteLabel: "system support option",
    new: "New System Support Option",
    edit: "Edit System Support Option",
    delete: "Delete System Support Option",
    search: "Search system support options",
  },

  common: {
    active: "Active",
    accept: "Accept",
    add: "Add",
    addPoints: "Add points",
    addNewItem: "Add new item",
    address: "Address",
    administration: "Administration",
    allow: "Allow",
    deny: "Deny",
    shared: "Shared",
    canEdit: "Can edit",
    alreadyPresent: "Already present",
    deduction: "Deduction",
    nominalAngle: "Nominal angle",
    deflection: "Deflection",
    horizontalDeduction: "Horizontal deduction",
    internalDeduction: "Internal deduction",
    externalDeduction: "External deduction",
    doubleTerminal: "Double terminal",
    doubleTerminalVisible: "Double terminal visible",
    factorPerFasteningPoint: "Factor per fastening point",
    factorPerLabel: "Factor per label",
    fasteningOption: "Fastening option",
    fasteningPoints: "Fastening points",
    fasteningPointsPerSupport: "Fastening points per support",
    floor: "Floor",
    overhead: "Overhead",
    wall: "Wall",
    none: "None",
    required: "Required",
    horizontal: "Horizontal",
    vertical: "Vertical",
    terminalTypeClosed: "Closed",
    terminalTypeOpen: "Open",
    jointHoleTemplateVisible: "Joint hole template visible",
    jointTemplate: "Joint template",
    junctions: "Junctions",
    junctionsRequired: "Junctions required",
    mountingContext: "Mounting context",
    plane: "Plane",
    quantity: "Quantity",
    quantityPerItem: "Quantity per item",
    quantityPerSupport: "Quantity per support",
    supportsRequired: "Supports required",
    hrailSupport: "Support",
    systemHeader: (title: string) => `System: ${title}`,
    terminalType: "Terminal type",
    tower: "Tower",
    workMethod: "Work method",
    fasteningOptionHeader: (title: string) => `Fastening Option: ${title}`,
    deleteEntityQuestion: (entity: string, label: string) => `Delete ${entity} "${label}"?`,
    angle: "Angle",
    angles: "Angles",
    archived: "Archived",
    atLeastOneItemNeeded: "At least one item is needed.",
    author: "Author",
    automaticSync: "Automatic sync",
    back: "Back",
    baseHeight: "Base height",
    bottomClamp: "Bottom clamp",
    bottomItems: "Bottom items",
    breadcrumb: "Breadcrumb",
    canBeCustom: "Can be custom",
    canBeMultiple: "Can be multiple",
    cancel: "Cancel",
    categories: "Categories",
    city: "City",
    clamped: "Clamped",
    clampThickness: "Clamp thickness",
    close: "Close",
    code: "Code",
    codeItem: "Code item",
    codeExtraItem: "Code extra item",
    company: "Company",
    componentMaterial: "Component material",
    componentMaterials: "Component materials",
    components: "Components",
    confirmed: "Confirmed",
    contactPerson: "Contact person",
    contactPersonWarning:
      "Entry of this data is voluntary and the full responsibility of the User. It is not recommended to enter data that identifies or makes persons identifiable.",
    cookiePolicy: "Cookie Policy",
    coordinates: "Coordinates",
    copy: "Copy",
    corners: "Corners",
    country: "Country",
    corrugationSpan: "Corrugation span",
    create: "Create",
    created: "Created",
    createNew: "Create new",
    currentRoofDoesNotAllowAnchorPoints: "The current roof configuration is not suitable for single anchor points.",
    curb: "Curb",
    curbHeight: "Curb height",
    curbDepth: "Curb depth",
    curbHeightBelowMinimum: (value: number) => `The minimum curb height for this selection is ${value} mm`,
    curbDepthBelowBaseHeight: (value: number) => `The curb depth must be at least ${value} mm to fit the selected base`,
    curve: "Curve",
    curved: "Curved",
    curves: "Curves",
    dataHasBeenSavedSuccessfully: "Data has been saved succesfully.",
    date: "Date",
    decrease: (value: number) => `Decrease ${value}`,
    download: "Download",
    default: "Default",
    defaultPitch: "Default pitch",
    defaultSupports: "Default supports",
    degrees: "Degrees",
    delete: "Delete",
    description: "Description",
    descriptionLong: "Description (long)",
    descriptionShort: "Description (short)",
    details: "Details",
    diameter: "Diameter",
    distance: "Distance",
    dragAndDropOrUploadAnImage: "Drag and Drop or click to upload an image",
    draft: "Draft",
    draw: "Draw",
    dropIt: "Drop it!",
    duplicate: "Duplicate",
    edit: "Edit",
    editCurve: "Edit curve",
    editPatrolComponent: "Edit patrol component",
    writePermissions: "Write permissions",
    editTerminal: "Edit terminal",
    editor: "Editor",
    email: "E-mail",
    erease: "Erease",
    error: "Error",
    errorLoadingData: "Error loading data",
    excel: "Excel",
    excludedItems: "Excluded items",
    extensions: "Extensions",
    extraItem: "Extra item",
    extras: "Extras",
    factor: "Factor",
    failed: "Failed",
    fieldCannotBeEmpty: "The field cannot be empty.",
    fieldContainsInvalidValueInsertBiggerOne: "The field contains an invalid value, insert a bigger one.",
    fieldContainsInvalidValueInsertSmallerOne: "The field contains an invalid value, insert a smaller one.",
    fieldValueInvalidFormat: "The value has an invalid format.",
    fieldInvalidValue: "The field contains an invalid value.",
    finish: "Finish",
    fixed: "Fixed",
    flatRoofCompatible: "Flat roof compatible",
    forTerminalsAndCurves: "For terminals and curves",
    forIntermediates: "For intermediates",
    forbidden: "Forbidden",
    forLoop: "For loop",
    forNotLoop: "For not loop",
    from: "From",
    general: "General",
    generalData: "General data",
    genericError: "An error has occurred, please try again.",
    globalSettings: "Global settings",
    guardrailComposition: "Guardrail composition",
    hasHips: "Has hips",
    height: "Height",
    image: "Image",
    heightToReach: "Height to reach",
    help: "Help",
    hidden: "Hidden",
    hide: "Hide",
    hipLength: "Hip length",
    hipsSpan: "Hips span",
    hipsSpans: "Hips spans",
    hipsSpanRequired: "Hips span required",
    home: "Home",
    includeSelectedComponent: "Include selected component",
    includeTarga: "Include targa",
    increase: (value: number) => `Increase ${value}`,
    installed: "Installed",
    insulationThickness: "Insulation thickness",
    insulationThicknessRequired: "Insulation thickness required",
    intermediate: "Intermediate",
    intermediates: "Intermediates",
    intermediateSupport: "Intermediate support",
    intersectionPoint: "Intersection point",
    invitationHasBeenSentSuccessfully: "Invitation has been sent successfully.",
    invite: "Invite",
    invitePage: {
      fallbackAuthorName: "a team member",
      invitedBy: "Invited by",
      invitationSentTo: "Invitation sent to",
      description: "Description",
      notAvailable: "Not available",
      acceptAction: "Accept invitation",
      rejectAction: "Reject invitation",
      notFoundTitle: "Invitation not found",
    },
    isAnchorPoint: "Is anchor point",
    isBallastRequired: "Is ballast required",
    isClampable: "Is clampable",
    isFasteningVisible: "Is fastening visible",
    isFlat: "Is flat",
    isGateVisible: "Is gate visible",
    isHighWindVisible: "Is high wind visible",
    isIntermediateBarRequired: "Is intermediate bar required",
    isLifeLine: "Is life line",
    baseOrientation: "Base orientation",
    cornerClassification: "Corner classification",
    isSubstructureIndependent: "Is roof substructure independent",
    isSubstructureRequired: "Is substructure required",
    isSubstructureDetailsRequired: "Is substructure details required",
    isPerimeterSplitRequired: "Is perimeter split required",
    isCornerOrientationRequired: "Is corner orientation required",
    isReclinableUpright: "Is reclining",
    isCurbVisible: "Is curb visible",
    isCurbDepthVisible: "Is curb depth visible",
    isReinforced: "Is reinforced",
    isToeBoardRequired: "Is toe board required",
    itemDependencies: "Item Dependencies",
    isVerticalBaseTypeRequired: "Is vertical base type required",
    isVertical: "Is vertical",
    isVisible: "Is visible",
    isWaterproofKitVisible: "Is waterproof kit visible",
    isWedgeVisible: "Is wedge visible",
    visibility: "Visibility",
    rothoblaasUsers: "Rothoblaas users",
    externalUsers: "External users",
    everyOne: "Everyone",
    ladderExitDifference: "Ladder exit difference",
    translations: "Translations",
    appLanguage: "App language",
    languages: {
      singular: "Language",
      plural: "Languages",
      arabic: "Arabic",
      czech: "Czech",
      danish: "Danish",
      german: "German",
      greek: "Greek",
      english: "English",
      spanish: "Spanish",
      estonian: "Estonian",
      finnish: "Finnish",
      french: "French",
      croatian: "Croatian",
      hungarian: "Hungarian",
      icelandic: "Icelandic",
      italian: "Italian",
      japanese: "Japanese",
      lithuanian: "Lithuanian",
      latvian: "Latvian",
      dutch: "Dutch",
      norwegian: "Norvegian",
      polish: "Polish",
      portugese: "Portugese",
      russian: "Russian",
      slovak: "Slovak",
      slovenian: "Slovenian",
      swedish: "Swedish",
      turkish: "Turkish",
      chinese: "Chinese",
      korean: "Korean",
    },
    firstName: "First name",
    lastAccess: "Last access",
    lastModified: "Last modified",
    lastName: "Last name",
    left: "Left",
    length: "Length",
    lastSync: "Last sync",
    lifeLine: "Lifeline",
    lifeLineAnchors: "Lifeline/Anchors",
    lifeLinePlusAnchorPoint: "Life line + anchor point",
    lifeLinesAndAnchorPointsHaveBeenFound_message:
      "Life lines and anchor points have been found. To edit the roof settings, you need to delete this data. Click 'Erease' to delete it and unlock the form",
    lifeLineSegment: "Lifeline segment",
    loading: "Loading...",
    location: "Location",
    loop: "Loop",
    login: "Login",
    logout: "Logout",
    lowerSupport: "Lower support",
    lowerHoleDiameter: "Lower hole diameter",
    mappings: "Mappings",
    max: "Max",
    maxAngle: "Max angle",
    maxCornersPerLifeline: "Max corners per lifeline",
    maxInsulationThickness: "Max insulation thickness",
    maxHeight: "Max height",
    maxPitch: "Max pitch",
    maxSeamsSpan: "Max seams span",
    maxSpan: "Max span",
    maxSpanBetweenCurveAndIntermediate: "Max span between curve and intermediates",
    maxSpanBetweenIntermediate: "Max span between intermediates",
    maxThickness: "Max thickness",
    maxUsers: "Max users",
    absorberAllowsMaxNUsers: (value: number) => `Absorber allows max ${value} users.`,
    metalPlateThickness: "Metal plate thickness",
    meters: "Meters",
    millimeters: " mm",
    min: "Min",
    minAngle: "Min angle",
    minimumConfigurableQuantity: "Minimum configurable quantity",
    minimumOrderQuantity: "Minimum order quantity",
    minMetalPlateThickness: "Min metal plate thickness",
    minSpanBetweenSupports: "Min span between supports",
    minSubstructureHeight: "Min substructure height",
    minSubstructureSize: "Min substructure size",
    minOrderQuantity: "Min order qty.",
    minThickness: "Min thickness",
    minCurbHeight: "Min curb height",
    minHeight: "Min height",
    minPitch: "Min pitch",
    minSpan: "Min span",
    minValue: "Min value",
    minWidth: "Min width",
    myArea: "My area",
    myProfile: "My Profile",
    name: "Name",
    next: "Next",
    never: "never",
    newConfigurationName: "New configuration name",
    nextNRows: (n: number) => `Next ${n} rows`,
    no: "No",
    noAdditionalPost: "No additional post",
    noItemsFound: "No items found.",
    normativeDetails: "Normative details",
    notFound: "Not found",
    notLoop: "Not loop",
    notRightAngle: "Not right angle",
    nrBallastIntermediate: "Nr. intermediate ballasts",
    nrBallastTerminal: "Nr. terminal ballasts",
    nrIntermediatesAndSpan: "Nr. intermediates and span",
    nrRivets: "Nr. rivets",
    nrOfFixingItemsRequired: "Nr of fixings items required (per wall material)",
    nrOfShuttlesForLifeLines: "Nr of shuttles for life lines",
    nrOfShuttlesRequestted: "Nr of shuttles requested",
    offer: "Offer",
    ok: "Ok",
    onlyAnchorPoint: "Only anchor point",
    onlyFor: "Only for",
    onlyImagesAreAllowed: "Only images are allowed.",
    onlyLifeLine: "Only life line",
    open: "Open",
    optional: "optional",
    optionals: "Optionals",
    order: "Order",
    overwrite: "Overwrite",
    perimeter: "Perimeter",
    closedPerimeter: "Closed perimeter",
    perimeterLength: "Total perimeter length",
    perimeterLengthParallel: "Length parallel to corrugation",
    perimeterLengthPerpendicular: "Length perpendicular to corrugation",
    cornerCountTotal: "Total corner count",
    cornerCountInternal: "Internal corner count",
    cornerCountExternal: "External corner count",
    parallel: "Parallel",
    perpendicular: "Perpendicular",
    internal: "Internal",
    external: "External",
    permissionToEdit: "Permission to edit",
    pitch: "Pitch",
    pitchMinMaxDefault: "Pitch (min/max/default)",
    pleaseSpecifyAHeight: "Please specify a height",
    pleaseSpecifyAMaxHeight: "Please specify a min height",
    pleaseSpecifyAMinHeight: "Please specify a max height",
    pleaseSpecifyAWidth: "Please specify a width",
    pleaseSpecifyAValueBetween: (min: number, max: number | null, unit: string) =>
      (max ? `Please specify a value between ${min} and ${max}` : `Please specify a value bigger than ${min}`) +
      (unit ? ` ${unit}` : "").trim(),
    pleaseSpecifyHeightAndWidth: (height: number, width: number, unit: string) =>
      `Please specify (height)x(width): ${height}x${width} ${unit}`,
    previousNRows: (n: number) => `Previous ${n} rows`,
    print: "Print",
    privacyPolicy: "Privacy Policy",
    qty: "Qty.",
    reject: "Reject",
    removable: "Removable",
    rename: "Rename",
    reclining: "Reclining",
    remove: "Remove",
    removeImage: "Remove image",
    reinforcedRailRequired: "Reinforced rail required",
    reset: "Reset",
    resetToDefault: "Reset to default",
    resourceNotFound: "The resource you are looking for has not been found.",
    right: "Right",
    rightAngle: "Right angle",
    roof: "Roof",
    roofInfo: "Roof info",
    roofs: "Roofs",
    roofWeight: "Roof weight",
    roofWeightRequired: "Roof weight required",
    save: "Save",
    saved: "Saved",
    seamHeight: "Seam height",
    seamHeightRequired: "Seam height required",
    seamsSpan: "Seams span",
    seamsSpanRequired: "Seams span required",
    search: "Search",
    searchIntermediatedCurves: "Search intermediated/curves",
    select: "Select",
    selectedComponent: "Selected component",
    selectProject: "Select project",
    selectProjectFirst: "Select project first",
    selectProjectInwhichCreateCopy: "Select the project in which to create the copy",
    sessionExpired: "Session expired",
    sessionExpiredText: "Please log in again to continue using the app.",
    settings: "Settings",
    sheetMaterialRequired: "Sheet material required",
    show: "Show",
    span: "Span",
    spans: "Spans",
    splitLifeLineHere: "Split life line here",
    status: "Status",
    stepHeight: "Step height",
    stepHeightRange: "Step height range",
    stepHeightRequired: "Step height required",
    straight: "Straight",
    substructureHeight: "Substructure height",
    substructures: "Substructures",
    substructureSize: "Substructure size",
    support: "Support",
    supportCurve: "Support curve",
    supports: "Supports",
    supportHeight: "Support height",
    supportMaterialRequired: "Support material required",
    supportIntermediateElement: "Support intermediate element",
    supportIntermediateElements: "Support intermediate elements",
    supportRequired: "Support required",
    supportSelection: "Support selection",
    supportsHeight: "Supports height",
    supportTerminal: "Support terminal",
    supportTerminalAndCurve: "Support terminal and curve",
    supportTerminalCurve: "Support terminal/curve",
    sync: "Sync",
    targa: "Targa",
    terminal: "Terminal",
    // terminal_plus_curve: "Terminal+curve",
    terminal_plus_intermediate: "Terminal+intermediate",
    terminal_plus_terminal: "Terminal+terminal",
    terminalAndCurve: "Terminal and curve",
    terminalCurves: "Terminals/curves",
    terminalCurveAndIntermediate: "Terminal, curve and intermediate",
    termsAndConditions: "Terms & Conditions",
    testEnvironment: "Test environment",
    theMaximumAllowedValueIs: (value: number) => `The maximum allowed value is ${value}°`,
    theMaximulAllowedLifeLineLengthExceeds: (
      meters: number,
      curves: number,
      maxLength: number,
      allowedLength: number,
    ) =>
      `The life line of the selected segment has a length of ${meters} meters. Due to the ${curves} curves the maximum allowed length of ${maxLength} meters is reduced to ${allowedLength}. Please split it.`,
    theMinAngleOfCurveForTheSystemIs_degrees: (degrees: number) =>
      `The current selected system does not allow to have curves tighter than ${degrees}°`,
    theMinAngleIfIntermediateAndTerminalforTheSystemIs_degrees: (degrees: number) =>
      `The current selected system with an intersection (terminal+intermediate) requires an almost straight line (min ${degrees}°)`,
    theMinimumAllowedValueIs: (value: number) => `The minimum allowed value is ${value}°`,
    theSystemRequiresAMinSpanOfXMeters: (minSpan: number) => `The system requires a min span of ${minSpan} meters`,
    toBeInstalled: "To be installed",
    to: "To",
    toInspect: "To inspect",
    topClamp: "Top clamp",
    topExtraItem: "Top extra item",
    topItem: "Top item",
    type: "Type",
    types: "Types",
    caps: "Caps",
    unlock: "Unlock",
    unshare: "Unshare",
    update: "Update",
    uploadedAt: "Uploaded",
    uploadedBy: "Uploaded by",
    upperHoleDiameter: "Upper hole diameter",
    upperSupport: "Upper support",
    useOnlyAnchorPoints: "Use only anchor points",
    value: "Value",
    versions: "Versions",
    visible: "Visible",
    warningThisEditCouldAffectOtherSystemsAsWell: "Warning. This edit could affect other systems as well!",
    wallFixings: "Wall fixings",
    waveSpan: "Wave span",
    waveSpanRequired: "Wave span required",
    width: "Width",
    yes: "Yes",
    youAreNotAllowed: "You are not allowed to access this resource.",
    youCannotMixUpSubstructureHeightSizeAndThickness:
      "Mixing substructure height, size and metal plate thickness is not allowed",
    youCannotMixUpSubstructureMaxSpanAndMaxInsulationThickness:
      "Mixing substructure max span and max insulation thickness is not allowed",
  },
};

export default en;
